import { ICompany, SecurityPermissionLevel } from '../../api-client/autogenerated';

export enum AssociationDocumentType {
  Drawings,
  Specifications,
}

export type AssociationDocument = {
  id: string;
  /** Sheet number for Drawings, section number (e.g. "03 30 00") for Specifications. */
  number: string;
  title?: string;
};

export type AssociationDocumentGroup = {
  /** Sheet prefix or division; '' for documents that have neither. */
  key: string;
  documents: AssociationDocument[];
};

const NO_PREFIX_KEY = '';

const isMissingNumber = (number: string) => !number.trim() || number.trim().toUpperCase() === 'N/A';

/**
 * The letters a sheet name starts with ("A" for A101, "AD" for AD-201), uppercased. Empty when the
 * name starts with anything else, or is missing. Discipline names are deliberately not guessed.
 */
export function getDrawingPrefix(sheetName: string): string {
  if (isMissingNumber(sheetName)) return NO_PREFIX_KEY;
  const match = /^[A-Za-z]+/.exec(sheetName.trim());
  return match ? match[0].toUpperCase() : NO_PREFIX_KEY;
}

/** The two-digit division a specification section number starts with, or '' if it has none. */
export function getSpecificationDivision(sectionNumber: string): string {
  if (isMissingNumber(sectionNumber)) return '';
  const match = /^\d{2}/.exec(sectionNumber.replace(/\s/g, ''));
  return match ? match[0] : '';
}

const compareNumbers = (a: AssociationDocument, b: AssociationDocument) =>
  a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });

/**
 * Groups documents by sheet prefix (Drawings) or division (Specifications). Groups are sorted by
 * key with the no-prefix / no-division group last; documents within a group by number.
 */
export function groupDocumentsForAssociation(
  type: AssociationDocumentType,
  documents: AssociationDocument[],
): AssociationDocumentGroup[] {
  const groups = new Map<string, AssociationDocumentGroup>();

  documents.forEach((document) => {
    const key =
      type === AssociationDocumentType.Drawings
        ? getDrawingPrefix(document.number)
        : getSpecificationDivision(document.number);
    let group = groups.get(key);
    if (!group) {
      group = { key, documents: [] };
      groups.set(key, group);
    }
    group.documents.push(document);
  });

  return Array.from(groups.values())
    .sort((a, b) => {
      if (a.key === b.key) return 0;
      if (a.key === '') return 1;
      if (b.key === '') return -1;
      return a.key.localeCompare(b.key);
    })
    .map((group) => ({ ...group, documents: [...group.documents].sort(compareNumbers) }));
}

/**
 * Whether every whitespace-separated word of `query` appears (case-insensitively) somewhere in the
 * fields. An empty query matches everything.
 */
export function matchesSearch(query: string, fields: (string | null | undefined)[]): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fields.join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/**
 * The companies' users that match the search on the user's name or email or on the name of the
 * user's company, so searching a company's name finds everyone in it. Companies left with no users
 * are dropped.
 */
export function filterCompaniesBySearch(companies: ICompany[], query: string): ICompany[] {
  if (!query.trim()) return companies;
  return companies
    .map((company) => ({
      ...company,
      users: company.users?.filter((user) =>
        matchesSearch(query, [company.name, user.name, user.email]),
      ),
    }))
    .filter(({ users }) => users && users.length > 0);
}

/**
 * The groups' documents that match the search on number, title or the group's own label (so
 * searching "Concrete" finds the whole division). Groups left with no documents are dropped.
 */
export function filterGroupsBySearch(
  groups: AssociationDocumentGroup[],
  query: string,
  getGroupLabel: (key: string) => string,
): AssociationDocumentGroup[] {
  if (!query.trim()) return groups;
  return groups
    .map((group) => ({
      ...group,
      documents: group.documents.filter((document) =>
        matchesSearch(query, [getGroupLabel(group.key), document.number, document.title]),
      ),
    }))
    .filter(({ documents }) => documents.length > 0);
}

/**
 * Whether a user can at least view a kind of document: an admin always can, anyone else needs a
 * security group that grants the document type a level at all. VIEW is the lowest level (0), so a
 * level of 0 counts and a missing entry -- no access -- does not.
 */
export function hasAtLeastViewPermission(
  level: SecurityPermissionLevel | null | undefined,
  isAdmin: boolean,
): boolean {
  return (
    isAdmin || (level !== null && level !== undefined && level >= SecurityPermissionLevel.NUMBER_0)
  );
}
